// ── Essentia.js Worker (client-side vocal analysis) ──────────

type WorkerCallback = (msg: { type: string; message?: string; data?: any; error?: string }) => void;

let essentiaWorker: Worker | null = null;
let essentiaCallbacks: WorkerCallback[] = [];

export function getEssentiaWorker(): Worker {
  if (!essentiaWorker) {
    essentiaWorker = new Worker('/essentia-worker.js');
    essentiaWorker.onmessage = (e) => {
      for (const cb of essentiaCallbacks) cb(e.data);
    };
  }
  return essentiaWorker;
}

/**
 * Decode an audio Blob into mono Float32Array + sampleRate using Web Audio API.
 */
export async function decodeAudioBlob(blob: Blob): Promise<{ audioData: Float32Array; sampleRate: number }> {
  const arrayBuf = await blob.arrayBuffer();
  const ctx = new AudioContext();
  const audioBuf = await ctx.decodeAudioData(arrayBuf);
  const chan0 = audioBuf.getChannelData(0); // Float32Array, mono
  ctx.close();
  return { audioData: chan0, sampleRate: audioBuf.sampleRate };
}

/**
 * Run analysis via Essentia.js worker. Returns the analysis JSON object.
 */
export function analyzeWithEssentia(
  audioData: Float32Array,
  sampleRate: number,
  onProgress?: (msg: string) => void,
): Promise<any> {
  return new Promise((resolve, reject) => {
    const worker = getEssentiaWorker();

    const handler: WorkerCallback = (msg) => {
      if (msg.type === 'progress') {
        onProgress?.(msg.message || '');
      } else if (msg.type === 'result') {
        cleanup();
        resolve(msg.data);
      } else if (msg.type === 'error') {
        cleanup();
        reject(new Error(msg.error || 'Worker error'));
      }
    };

    const cleanup = () => {
      essentiaCallbacks = essentiaCallbacks.filter((cb) => cb !== handler);
    };

    essentiaCallbacks.push(handler);
    worker.postMessage({ type: 'analyze', audioData, sampleRate });
  });
}

/**
 * Analyze audio entirely client-side using Essentia.js WASM.
 */
export async function analyzeAudio(
  blob: Blob,
  onProgress?: (msg: string) => void,
): Promise<any> {
  onProgress?.('Decoding audio...');
  const { audioData, sampleRate } = await decodeAudioBlob(blob);
  return analyzeWithEssentia(audioData, sampleRate, onProgress);
}
